// Library imports
import React, { useMemo, useState } from 'react';
import { MDBCard, MDBCardBody, MDBCardHeader, MDBBtn, MDBIcon, MDBBtnGroup } from 'mdb-react-ui-kit';
import { useSelector } from 'react-redux';
import moment from 'moment';
// Component imports
import NoData from '../../../../../components/NoData';
import PropBarChart from '../../../../../components/Graphs/PropBarChart';
// Styled components
import { InnerContent } from '../../styles';
import { BodyHeader, PlayerHeader, PlayerName, PlayerPosition, StatHighlight } from './styles';
// Utils
import { formatOdd } from '../../../../../utils/odds';
import { getRoundTo } from '../../KeyFactors/utils';

const PropInsights = ({ playerData, selectedProp, setView, isModal = false, game = null }) => {
    const propData = playerData?.props ? playerData.props.find(p => p.id === selectedProp) : null;
    // Redux
    const { sport } = useSelector(state => state.app);
    // Local state
    const [numberOfGames, setNumberOfGames] = useState(5);
    const [filterByOpponent, setFilterByOpponent] = useState(false);

    // Only show play time for NBA and NHL
    const showPlayTime = sport === 'nba' || sport === 'nhl';

    // Determine opponent teamId from game
    const opponentTeamId = useMemo(() => {
        if (!game || !playerData?.teamId) return null;
        // Return the opposite team's ID from the game
        return playerData.teamId === game.awayTeam?.teamId ? game.homeTeam?.teamId : game.awayTeam?.teamId;
    }, [game, playerData]);

    const chartData = useMemo(() => {
        if (!playerData?.gameLogs || !propData?.connectedStat) return [];

        // Filter by opponent if enabled and opponentTeamId is available
        let filteredLogs = playerData.gameLogs;
        if (filterByOpponent && opponentTeamId) {
            filteredLogs = playerData.gameLogs.filter(log => log.opponentId === opponentTeamId);
        }

        return filteredLogs
            .slice(0, numberOfGames)
            .reverse()
            .map((log, index) => ({
                game: `${log.side === 'away' ? '@' : ''}${log.opponent}|${moment(log.date, 'M/D/YYYY').format('M/D')}|${index}`,
                [propData.connectedStat]: propData.connectedStats
                    ? propData.connectedStats.reduce((acc, stat) => acc + (log[stat]?.value ?? 0), 0)
                    : (log[propData.connectedStat]?.value ?? 0),
                ...(showPlayTime && { playTimePerGame: log.playTimePerGame?.value ?? 0 }),
            }));
    }, [playerData, propData?.connectedStat, propData?.connectedStats, numberOfGames, showPlayTime, filterByOpponent, opponentTeamId]);

    if (!playerData?.stats || !propData) {
        return (
            <InnerContent fluid className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '300px' }}>
                <NoData title="Player Data" message={`No data found for ${playerData?.name || 'this player'}. Please select another.`} />
                {!isModal && (
                    <MDBBtn className="mt-3" color="secondary" onClick={() => setView('roster')}>
                        <MDBIcon fas icon="arrow-left" className="me-2" /> Back to Roster
                    </MDBBtn>
                )}
            </InnerContent>
        );
    }

    return (
        <InnerContent fluid>
            <MDBCard
                className="mb-3 shadow-2-strong"
                style={{
                    background: 'rgba(15, 23, 42, 0.88)',
                    border: '1px solid rgba(148, 163, 184, 0.24)',
                    borderRadius: 14,
                    overflow: 'hidden',
                    color: '#e2e8f0',
                }}
            >
                <MDBCardHeader
                    style={{
                        background: 'linear-gradient(160deg, rgba(30,41,59,0.85) 0%, rgba(15,23,42,0.95) 100%)',
                        borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
                    }}
                >
                    <PlayerHeader>
                        <div>
                            <PlayerName>
                                {playerData.name} | {playerData.position}
                            </PlayerName>
                            <PlayerPosition>
                                <span className="text-muted">
                                    {propData?.name || 'Unknown Prop'} {propData.value || 'NA'}{' '}
                                    {propData.type === 'over-under' && `(${formatOdd(propData?.overOdds, 'moneyline')})`}
                                </span>
                            </PlayerPosition>
                        </div>
                        {!isModal && (
                            <MDBBtn color="secondary" size="sm" onClick={() => setView('player-data')}>
                                <MDBIcon fas icon="arrow-left" className="me-2" /> Back
                            </MDBBtn>
                        )}
                    </PlayerHeader>
                </MDBCardHeader>
                <MDBCardBody className="py-1" style={{ background: 'rgba(15, 23, 42, 0.5)' }}>
                    <BodyHeader>
                        {playerData?.stats?.[propData.connectedStat] && (
                            <StatHighlight>
                                <span>{playerData.stats[propData.connectedStat] ? playerData.stats[propData.connectedStat].label : 'Unknown'}:</span>
                                <strong>
                                    {' '}
                                    {playerData.stats[propData.connectedStat]?.value
                                        ? playerData.stats[propData.connectedStat].value.toFixed(getRoundTo(propData.connectedStat))
                                        : 'NA'}
                                </strong>
                            </StatHighlight>
                        )}
                        <div className="d-flex gap-2 align-items-center">
                            {opponentTeamId && (
                                <MDBBtn
                                    size="sm"
                                    color={filterByOpponent ? 'success' : 'light'}
                                    onClick={() => setFilterByOpponent(!filterByOpponent)}
                                    title="Filter by upcoming opponent"
                                >
                                    <MDBIcon fas icon="filter" className="me-1" />
                                    vs {game?.awayTeam?.teamId === playerData?.teamId ? game?.homeTeam?.abbr : game?.awayTeam?.abbr}
                                </MDBBtn>
                            )}
                            <MDBBtnGroup>
                                {[5, 10, 15].map(n => (
                                    <MDBBtn key={n} size="sm" color={numberOfGames === n ? 'primary' : 'light'} onClick={() => setNumberOfGames(n)}>
                                        {n}
                                    </MDBBtn>
                                ))}
                            </MDBBtnGroup>
                        </div>
                    </BodyHeader>
                    <PropBarChart
                        data={chartData}
                        xAxisLabel="game"
                        yAxisLabel={playerData.stats[propData.connectedStat]?.label || 'Value'}
                        yAxisKey={propData.connectedStat}
                        symbol=""
                        fixedAmount={getRoundTo(propData.connectedStat)}
                        height="12rem"
                        propData={propData}
                    />
                </MDBCardBody>
            </MDBCard>
        </InnerContent>
    );
};

export default PropInsights;
